/**
 * Utility functions for error handling and validation
 */

/**
 * Normalizes an error (string, array, or object) to a displayable string for dialogs/toasts.
 *
 * Handles the common NestJS error shapes:
 *   - plain string
 *   - { statusCode, message: string, error?: string }   (e.g. BadRequestException)
 *   - { statusCode, message: string[], error?: string } (e.g. ValidationPipe)
 *   - axios-style { response: { data: ... } } as a final fallback
 */
export const getErrorMessage = (error: unknown): string => {
  const fallback = 'An unexpected error occurred'

  if (error == null) {
    return fallback
  }
  if (typeof error === 'string') {
    return error.trim() || fallback
  }
  if (Array.isArray(error)) {
    const joined = error.filter(Boolean).map(String).join(', ')

    return joined || fallback
  }
  if (typeof error === 'object') {
    const e = error as {
      message?: unknown
      error?: unknown
      response?: { data?: unknown }
    }

    if (Array.isArray(e.message)) {
      const joined = (e.message as unknown[]).filter(Boolean).map(String).join(', ')
      if (joined) return joined
    }
    if (typeof e.message === 'string' && e.message.trim()) {
      return e.message
    }
    if (typeof e.error === 'string' && e.error.trim()) {
      return e.error
    }
    if (e.response?.data) {
      return getErrorMessage(e.response.data)
    }
  }

  return fallback
}

/**
 * Checks if an error payload represents an unauthorized (403) or forbidden (401) error.
 * This function handles various error formats that may come from different API clients or error handlers.
 *
 * @param errorPayload - The error object or string to check. Can be:
 *   - An error object with statusCode, status, error, response, or message properties
 *   - A string error message
 *   - null or undefined
 *
 * @returns true if the error is a 403 Forbidden or 401 Unauthorized error, false otherwise
 *
 * @example
 * ```typescript
 * const error = { statusCode: 403, message: 'Access denied' }
 * if (isUnauthorizedError(error)) {
 *   router.replace(ROUTES.UNAUTHORIZED)
 * }
 * ```
 */
export const isUnauthorizedError = (errorPayload: any): boolean => {
  if (!errorPayload) {
    return false
  }

  // Handle string errors - check if message contains unauthorized/forbidden keywords
  if (typeof errorPayload === 'string') {
    const lowerMessage = errorPayload.toLowerCase()

    return (
      lowerMessage.includes('forbidden') ||
      lowerMessage.includes('unauthorized') ||
      lowerMessage.includes('does not have access')
    )
  }

  // Handle object errors
  if (typeof errorPayload === 'object' && errorPayload !== null) {
    // Check statusCode (common in API error responses)
    if (errorPayload.statusCode === 403 || errorPayload.statusCode === 401) {
      return true
    }

    // Check error property (common in HTTP error objects)
    if (errorPayload.error === 'Forbidden' || errorPayload.error === 'Unauthorized') {
      return true
    }

    // Check response.status (common in axios errors)
    if (errorPayload.response?.status === 403 || errorPayload.response?.status === 401) {
      return true
    }

    // Check status property (alternative error format)
    if (errorPayload.status === 403 || errorPayload.status === 401) {
      return true
    }

    // Check message content for unauthorized/forbidden keywords
    if (errorPayload.message) {
      const lowerMessage = String(errorPayload.message).toLowerCase()
      if (
        lowerMessage.includes('forbidden') ||
        lowerMessage.includes('unauthorized') ||
        lowerMessage.includes('does not have access')
      ) {
        return true
      }
    }
  }

  return false
}
